import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import LockIcon from '@mui/icons-material/Lock';

type TooltipIconButtonProps = {
    onPress: () => void;
    isAvailable: boolean;
    tooltipTitle: string;
    tooltipMessage?: string;
    relatedLink?: string;
    icon: React.ReactNode;
    isLocked?: boolean;
};

/**
 * ツールチップ付きアイコンボタン
 */
/**
 * ツールチップ付きアイコンボタンコンポーネント
 * 
 * アイコンボタンにツールチップを表示し、ロック状態や利用可能状態を視覚的に表現します。
 * ツールチップ内にはタイトル、メッセージ、関連リンクを含めることができます。
 * 
 * @param props - TooltipIconButtonProps
 * @param props.onPress - ボタンがクリックされた時のコールバック関数
 * @param props.isAvailable - ボタンが利用可能かどうかのフラグ。falseの場合、ボタンは無効化される
 * @param props.tooltipTitle - ツールチップのタイトルテキスト
 * @param props.tooltipMessage - ツールチップの詳細メッセージ（オプション）
 * @param props.relatedLink - ツールチップ内に表示する関連リンクのURL（オプション）
 * @param props.icon - 表示するアイコン要素
 * @param props.isLocked - ロック状態を示すフラグ。trueの場合、右下に鍵アイコンが表示される（デフォルト: false）
 * 
 * @returns ツールチップ付きアイコンボタンのJSX要素
 * 
 * @example
 * ```tsx
 * <TooltipIconButton
 *   onPress={() => console.log('clicked')}
 *   isAvailable={true}
 *   tooltipTitle="機能名"
 *   tooltipMessage="この機能の説明"
 *   relatedLink="https://example.com"
 *   icon={<SomeIcon />}
 *   isLocked={false}
 * />
 * ```
 */
const TooltipIconButton: React.FC<TooltipIconButtonProps> = ({
    onPress,
    isAvailable,
    tooltipTitle,
    tooltipMessage,
    relatedLink,
    icon,
    isLocked = false,
}) => {
    // ツールチップ内容生成
    const renderTooltipContent = () => (
        <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
            {tooltipTitle}
            {tooltipMessage && (
                <>
                    <br />
                    {tooltipMessage}
                </>
            )}
            {relatedLink && (
                <>
                    <br />
                    <a
                        href={relatedLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#80d8ff' }}
                    >
                        詳細はこちら
                    </a>
                </>
            )}
        </div>
    );

    return (
        <Tooltip title={renderTooltipContent()} arrow>
            <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}>
                <IconButton
                    edge="end"
                    size="medium"
                    sx={{ mr: 0 }}
                    disabled={!isAvailable}
                    onClick={onPress}
                >
                    <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}>
                        {icon}
                        {isLocked && (
                            <LockIcon
                                sx={{
                                    position: 'absolute',
                                    bottom: '-2px', // 鍵アイコンを右下に配置
                                    right: '-2px', // 鍵アイコンを右下に配置
                                    color: '#00000080',
                                    fontSize: '60%', // 鍵アイコンは親コンポーネントの60%サイズで表示
                                    pointerEvents: 'none',
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '50%', // 背景を円形にする
                                    padding: '5%',
                                }}
                            />
                        )}
                    </span>
                </IconButton>
            </span>
        </Tooltip>
    );
};

export default TooltipIconButton;
